import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import SupplierManagementShell from "./SupplierManagementShell";
import "./supplierManagementTables.css";
import { AssessmentService } from "../../../../_services/company/assessmentService";
import { usePermission } from "../../../../hooks/usePermission";
import AssessmentFilterBar from "../assessment/AssessmentFilterBar";
import AssessmentEmptyState from "../assessment/AssessmentEmptyState";
import {
  primaryBtn,
  secondaryBtn,
  tableCardSurface,
  thStyle,
} from "../assessment/assessmentStyles";
import UserAssessmentDelegationModal from "./components/UserAssessmentDelegationModal";

/**
 * Supplier-side User Management page. Lists the team members registered
 * under the caller's supplier and, for Supplier Admins, exposes the
 * delegation UX per user (which received assessments they can act on).
 * Layout mirrors MyAssignmentsByCompany so the pages read as one product.
 */
export default function SupplierUserManagement() {
  const location = useLocation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [delegateTarget, setDelegateTarget] = useState(null);
  const [canDelegate] = usePermission("supplier.users.assign_assessment");

  const loadUsers = async () => {
    setLoading(true);
    const { isSuccess, data } = await AssessmentService.listSupplierUsers();
    setLoading(false);
    if (isSuccess) {
      setUsers(data?.data ?? []);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const hay = [u.first_name, u.last_name, u.email, u.mobile_number]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [users, search]);

  return (
    <SupplierManagementShell
      pathname={location.pathname}
      pageTitle="User Management"
      totalCount={filtered.length}
      actionsSlot={
        <button type="button" style={secondaryBtn} onClick={loadUsers}>
          Refresh
        </button>
      }
    >
      <AssessmentFilterBar
        rows={users}
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by name, email, mobile…"
        showStatus={false}
      />

      <div className="supplier-mgmt-table-wrap" style={tableCardSurface}>
        <div className="supplier-mgmt-table-scroll">
          <table className="supplier-mgmt-table table">
            <thead>
              <tr>
                <th style={{ ...thStyle, width: 50 }}>#</th>
                <th style={thStyle}>Name</th>
                <th style={thStyle}>Email</th>
                <th style={{ ...thStyle, width: 160 }}>Mobile</th>
                <th style={{ ...thStyle, width: 120 }}>Country</th>
                <th style={{ ...thStyle, width: 190, textAlign: "right" }}></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="text-center text-muted py-4">
                    Loading…
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ padding: 0 }}>
                    <AssessmentEmptyState
                      title="No team members"
                      message={
                        users.length === 0
                          ? "Invite additional users through the buyer's invitation flow to build your team."
                          : "Try adjusting the search term."
                      }
                      hasFilters={Boolean(search)}
                      onClearFilters={() => setSearch("")}
                    />
                  </td>
                </tr>
              ) : (
                filtered.map((u, idx) => {
                  const name =
                    [u.first_name, u.last_name].filter(Boolean).join(" ") ||
                    "—";
                  return (
                    <tr key={u.id}>
                      <td>{idx + 1}</td>
                      <td style={{ fontWeight: 600, color: "var(--ink)" }}>
                        {name}
                      </td>
                      <td>{u.email || "—"}</td>
                      <td>{u.mobile_number || "—"}</td>
                      <td>{u.country || "—"}</td>
                      <td style={{ textAlign: "right" }}>
                        {canDelegate ? (
                          <button
                            type="button"
                            onClick={() =>
                              setDelegateTarget({
                                userId: u.id,
                                name: name === "—" ? u.email : name,
                                email: u.email,
                              })
                            }
                            style={{
                              ...primaryBtn,
                              height: 28,
                              padding: "0 14px",
                              fontSize: 12,
                              lineHeight: "26px",
                              cursor: "pointer",
                            }}
                          >
                            Manage Assessments
                          </button>
                        ) : (
                          <span style={{ color: "var(--ink-4)", fontSize: 12 }}>
                            —
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <UserAssessmentDelegationModal
        show={!!delegateTarget}
        user={delegateTarget}
        onClose={() => setDelegateTarget(null)}
        onSaved={() => setDelegateTarget(null)}
      />
    </SupplierManagementShell>
  );
}
